"use client";

import { useState, useEffect, useRef, forwardRef, Ref } from "react";
import Link from "next/link";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { History, X } from "lucide-react";
import productsminData from "@/public/ecomsaas-assets/cache/products.json";
import categoryData from "@/public/ecomsaas-assets/cache/category.json";

// Define type for products based on your JSON structure
interface Product {
    id: number;
    name: string;
    eng_name?: string;
    imageurl: string;
    seo_url: string;
    product_tags?: string;
    sku?: string;
    category_id: number;
    visibility?: string;
}

// Define type for categories based on your JSON structure
interface Category {
    id: number;
    name: string;
    seo_url: string;
}

// Define props for SearchBar
interface SearchBarProps {
    isMobile: boolean;
    onNavigate: (path: string) => void;
}

const SearchBar = forwardRef(
    ({ isMobile, onNavigate }: SearchBarProps, ref: Ref<{ focusInput: () => void }>) => {
        const [searchQuery, setSearchQuery] = useState("");
        const [suggestions, setSuggestions] = useState<Product[]>([]);
        const [categorySuggestions, setCategorySuggestions] = useState<Category[]>([]);
        const [recentSearches, setRecentSearches] = useState<string[]>([]);
        const [showSuggestions, setShowSuggestions] = useState(false);
        const searchRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);

        const assets = process.env.NEXT_PUBLIC_ASSETSSTORE || "";

        // Expose focusInput method to parent via ref
        useEffect(() => {
            if (ref) {
                (ref as any).current = {
                    focusInput: () => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    },
                };
            }
        }, [ref]);

        // Load recent searches from localStorage on mount
        useEffect(() => {
            const storedSearches = JSON.parse(localStorage.getItem("recentSearches") || "[]");
            setRecentSearches(storedSearches);
        }, []);

        // Prevent page scrolling when dropdown is open
        useEffect(() => {
            if (showSuggestions) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "auto";
            }
            return () => {
                document.body.style.overflow = "auto";
            };
        }, [showSuggestions]);

        // Autofocus input when suggestions are shown
        useEffect(() => {
            if (showSuggestions && inputRef.current) {
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 0);
            }
        }, [showSuggestions]);

        const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const query = e.target.value.toLowerCase();
            setSearchQuery(query);

            if (query.length < 1) {  // <--- changed from 2 to 1
                setSuggestions([]);
                setCategorySuggestions([]);
                setShowSuggestions(query.length === 0 && recentSearches.length > 0);
                return;
            }

            const filteredProducts = (productsminData as any[])
                .filter((product) => product.visibility === 1)
                .filter((product) =>
                    [product.name, product.eng_name, product.seo_url, product.product_tags, product.sku]
                        .some((field) => field?.toLowerCase().includes(query))
                )
                .map((product) => ({
                    id: product.id,
                    name: product.name,
                    imageurl: product.imageurl,
                    seo_url: product.seo_url,
                    category_id: product.category_id,
                }))
                .sort((a: Product, b: Product) => a.name.localeCompare(b.name))
                .slice(0, 5);

            const filteredCategories = (categoryData as Category[])
                .filter((category) => {
                    const matchesCategory = [category.name, category.seo_url].some(
                        (field) => field?.toLowerCase().includes(query)
                    );

                    const matchesProduct = (productsminData as any[]).some(
                        (product) =>
                            product.category_id === category.id &&
                            product.visibility === 1 &&
                            [product.name, product.eng_name, product.seo_url, product.product_tags, product.sku]
                                .some((field) => field?.toLowerCase().includes(query))
                    );

                    return matchesCategory || matchesProduct;
                })
                .slice(0, 5);

            setSuggestions(filteredProducts);
            setCategorySuggestions(filteredCategories);
            setShowSuggestions(true);
        };


        const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (searchQuery.trim()) {
                // Store recent search in localStorage
                let recent = JSON.parse(localStorage.getItem("recentSearches") || "[]");
                if (!recent.includes(searchQuery)) {
                    recent.unshift(searchQuery);
                    recent = recent.slice(0, 5); // Keep only the latest 5 searches
                    localStorage.setItem("recentSearches", JSON.stringify(recent));
                    setRecentSearches(recent);
                }
                onNavigate(`/products?search=${encodeURIComponent(searchQuery)}`);
                setShowSuggestions(false);
            }
        };

        // Handle clicking a recent search
        const handleRecentSearchClick = (query: string) => {
            setSearchQuery(query);
            onNavigate(`/products?search=${encodeURIComponent(query)}`);
            setShowSuggestions(false);
        };

        // Handle clicking a product
        const handleProductClick = (product: Product) => {
            let recent = JSON.parse(localStorage.getItem("recentSearches") || "[]");
            if (!recent.includes(product.name)) {
                recent.unshift(product.name);
                recent = recent.slice(0, 5); // Keep only the latest 5 searches
                localStorage.setItem("recentSearches", JSON.stringify(recent));
                setRecentSearches(recent);
            }
            setShowSuggestions(false);
            onNavigate(`/p/${product.seo_url}`);
        };

        // Clear all recent searches
        const handleClearAll = () => {
            localStorage.setItem("recentSearches", JSON.stringify([]));
            setRecentSearches([]);
            setShowSuggestions(false);
        };

        // Remove a single recent search
        const handleRemoveSearch = (query: string) => {
            const updatedSearches = recentSearches.filter((search) => search !== query);
            localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
            setRecentSearches(updatedSearches);
            if (updatedSearches.length === 0) {
                setShowSuggestions(false);
            }
        };

        // Close suggestions on click outside
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                    setShowSuggestions(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        const getFirstImageUrl = (imageurl: string | string[]): string => {
            if (Array.isArray(imageurl)) {
                return imageurl[0] || "/placeholder.svg";
            }
            const firstImage = imageurl?.split(",")[0]?.trim();
            return firstImage || "/placeholder.svg";
        };

        // Truncate text to 40 characters
        const truncateText = (text: string, maxLength: number = 25): string => {
            if (text.length <= maxLength) return text;
            return text.slice(0, maxLength) + "...";
        };

        // Calculate dropdown width to match input width
        const dropdownWidth = inputRef.current ? `${inputRef.current.offsetWidth}px` : "100%";

        return (
            <div
                ref={searchRef}
                className={isMobile ? "my-4 w-full pb-3 px-4 flex items-center" : "my-3 w-full flex items-center justify-center"}
            >
                <form className="relative w-full rounded-md mx-auto" onSubmit={handleSearchSubmit}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search Products..."
                        className={`w-full ${isMobile ? " border-2 border-gray-200" : ""} rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:rounded-md`}
                        aria-label="Search products"
                        id={isMobile ? "searchInputMobile" : "searchInput"}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => {
                            setShowSuggestions(recentSearches.length > 0 || searchQuery.trim().length >= 2);
                        }}
                    />
                    <button
                        type="submit"
                        className="Header-btn absolute right-0 top-0 h-full px-4 bg-prring-primary rounded-r-md hidden"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                    </button>

                    {showSuggestions && (
                        <div
                            className="absolute z-50 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-80 overflow-y-auto"
                            style={{ width: dropdownWidth }}
                        >
                            {searchQuery.length === 0 && recentSearches.length > 0 ? (
                                <>
                                    <div className="flex justify-between items-center px-4 py-2">
                                        <p className="text-sm text-gray-700">Recent Searches</p>
                                        <button
                                            onClick={handleClearAll}
                                            className="text-sm text-red-500 hover:text-red-700"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2 p-2 justify-start flex-wrap">
                                        {recentSearches.map((search, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between rounded-full p-2 bg-blue-600/20 hover:bg-gray-100"
                                            >
                                                <Link
                                                    href={`/products?search=${encodeURIComponent(search)}`}
                                                    className="flex whitespace-nowrap items-center gap-2 text-[11px] md:text-sm text-gray-800"
                                                    onClick={() => handleRecentSearchClick(search)}
                                                >
                                                    <History className="h-4 w-4" /> {truncateText(search)}
                                                </Link>
                                                <button
                                                    onClick={() => handleRemoveSearch(search)}
                                                    className="ml-2 text-gray-500 hover:text-red-500"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {suggestions.length > 0 ? (
                                        <>
                                            <p className="text-sm text-gray-700 px-4 py-2">Products</p>
                                            {suggestions.map((product) => (
                                                <Link
                                                    key={product.id}
                                                    href={`/p/${product.seo_url}`}
                                                    className="flex items-center p-3 hover:bg-gray-100"
                                                    onClick={() => handleProductClick(product)}
                                                >
                                                    <LazyLoadImage
                                                        src={`${assets}${getFirstImageUrl(product.imageurl)}`}
                                                        alt={product.name}
                                                        width={40}
                                                        height={40}
                                                        className="object-cover rounded-md mr-3"
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                                            e.currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
                                                        }}
                                                    />
                                                    <span className="text-sm text-gray-800">{truncateText(product.name)}</span>
                                                </Link>
                                            ))}
                                        </>
                                    ) : (
                                        <></>
                                    )}

                                    {categorySuggestions.length > 0 ? (
                                        <>
                                            <p className="text-sm text-gray-700 px-4 py-2">Categories</p>
                                            <div className="flex items-center flex-wrap gap-2 p-2">
                                                {categorySuggestions.map((category) => (
                                                    <Link
                                                        key={category.id}
                                                        href={`/products/${category.seo_url}`}
                                                        className="flex items-center whitespace-nowrap rounded-full bg-primary/20 p-2 px-3 hover:bg-gray-100"
                                                        onClick={() => {
                                                            setShowSuggestions(false);
                                                            onNavigate(`/products/${category.seo_url}`);
                                                        }}
                                                    >
                                                        <span className="text-sm text-gray-800">{truncateText(category.name)}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        suggestions.length === 0 && (
                                            <></>
                                        )
                                    )}

                                    {suggestions.length === 0 && categorySuggestions.length === 0 && (
                                        <Link
                                            href="/products"
                                            className="block text-sm text-gray-700 px-4 py-2 hover:bg-gray-100"
                                            onClick={() => {
                                                setShowSuggestions(false);
                                                onNavigate('/products');
                                            }}
                                        >
                                            No results found. <span className="text-blue-800 underline"> Browse all </span> products?
                                        </Link>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </form>
            </div>
        );
    }
);

SearchBar.displayName = "SearchBar";

export default SearchBar;